import _ from "underscore";
import DriveView from "../../DriveView";
import {NetworkStates, PageLevel} from "../../store/mobile/types";
import Backbone from "backbone";
import pageHTML from "./emailLogs.tmpl";
import {getEldProviderName, isEldTestTransferEnabled, isEldTransferValidateOnly} from "../../utils/helpers";
import {isIphone} from "../../utils/native/nativeHelpers";
import {CanadaEldOperatingSystems} from "hos-tools";
import {show, hide} from "../../utils/vanilla";
import {BigSpinner} from "../../utils/spinner";
import {IApiError} from "../../utils/api.interfaces";
import toastr from "../../utils/toastr";
import { DAYS, SECONDS, MINUTES } from "../../constants";
import {observeStore} from "../../utils/store";
import { dstCalculator } from "../../utils/date";
import { IDataStore, ServerFlags } from "../../store/data/types";

export enum EldResponseStatus {
    Success = "Success",
    Error = "Error",
    Warning = "Warning",
    Information = "Information",
    NotValidated = "NotValidated",
    InvalidRegistrationData = "InvalidRegistrationData",
    EldDecertified = "EldDecertified",
    Timeout = "Timeout"
}

export interface IEldResponse {
    broadcast?: string;
    status: EldResponseStatus;
    errorMessage?: string;
    isDelivered: boolean;
}

export default class EmailHOSLogsView extends DriveView<Backbone.Model> {
    readonly template = _.template(pageHTML);
    readonly spinner = new BigSpinner({
        left: "9px",
        top: "9px"
    });
    readonly userEmail: string | undefined;
    currentTimeInMS: number;
    errorBox: HTMLTableRowElement | null = null;
    errorText: HTMLSpanElement | null = null;
    spinnerContainer: HTMLDivElement | null = null;
    transferSpinnerContainer: HTMLDivElement | null = null;
    transferTable: HTMLTableElement | null = null;
    fromDatePicker: HTMLInputElement | null = null;
    toDatePicker: HTMLInputElement | null = null;

    protected strings = [
        "Enter a valid email.",
        "Logs cannot be from more than one year ago.",
        "The date range must be 31 days or shorter.",
        "Logs sent to {0} for the selected date range",
        "Email not sent, try again later",
        "The date “from” must be earlier than the date “to”.",
        "The selected date is incomplete or incorrect.",
        "Logs cannot be from a future date.",
        "Too many recent attempts to send HOS logs, try again later."
    ];
    constructor (options) {
        super(options, {
            title: "Email HOS logs",
            pageLevel: PageLevel.TWO
        });

        // Set User Email
        const currentUser = this.app.userService.getCurrentUser();
        this.userEmail = currentUser ? currentUser.name : "Enter Email Here";

        // Set Current Time
        this.currentTimeInMS = dstCalculator.toLocalDateTime(new Date()).getTime();

        // Observe changes to the online state
        observeStore(this.app.store, state => state.mobile.online, () => {
            if (this.app.store.getState().mobile.online === NetworkStates.ONLINE) {
                this.enableSendButton(true);
            } else {
                this.enableSendButton(false);
            }
        });
    }

    // Helper Function to show an error with the corresponding message
    showError(msg: string) {
        this.getHTMLElements();
        if (this.errorBox !== null && this.errorText !== null) {
            show(this.errorBox);
            this.errorText.textContent = msg;
        }
    }

    hideError() {
        this.getHTMLElements();
        if (this.errorBox !== null) {
            hide(this.errorBox);
        }
    }

    handleResponse(response: IEldResponse, emailAddress: string) {
        switch (response.broadcast) {
            case "Email transfer sent successfully":
                this.hideError();
                toastr.success(this.getString("Logs sent to {0} for the selected date range").replace("{0}", emailAddress));
                break;
            default:
                toastr.error(this.getString("Email not sent. Refresh the page and try again."));
                break;
        }
    }

    handleError(error: IApiError) {
        // Catch Rate Limit Exception
        if (error.data.type === "OverLimitException") {
            toastr.error(this.getString("Too many recent attempts to send HOS logs, try again later."));
            return;
        }

        // Check remaining errors
        switch (error.message) {
            case "The recipient email address is invalid":
                toastr.error(this.getString("Enter a valid email."));
                break;
            case "Logs cannot be from more than one year ago.":
                toastr.error(this.getString("Logs cannot be from more than one year ago."));
                break;
            case "The date range must be 31 days or shorter.":
                toastr.error(this.getString("The date range must be 31 days or shorter."));
                break;
            case "From date is after To date":
                toastr.error(this.getString("The date “from” must be earlier than the date “to”."));
                break;
            default:
                toastr.error(this.getString("Email not sent. Refresh the page and try again."));
                break;
        }
    }

    roadSideCheckTransfer() {
        this.app.router.navigate("inspection/main", true);
    }

    // Helper Function to hide the table and show the spinner
    showSpinner() {
        this.spinner.spin(this.spinnerContainer);
        if (this.transferSpinnerContainer !== null && this.transferTable !== null) {
            hide(this.transferTable);
            show(this.transferSpinnerContainer);
        }
    }

    // Helper Function to hide the spinner and show the table
    showTable() {
        this.spinner.stop();
        if (this.transferSpinnerContainer !== null && this.transferTable !== null) {
            hide(this.transferSpinnerContainer);
            show(this.transferTable);
        }
    }

    enableSendButton(enable: boolean) {
        if (enable) {
            document.getElementById("sendEmail")?.removeAttribute("disabled");
        } else {
            document.getElementById("sendEmail")?.setAttribute("disabled", "true");
        }
    }

    checkValidRange() {
        // Ensure Elements exist
        this.getHTMLElements();

        // Check if any part of date is incomplete
        if (this.fromDatePicker?.value === "" || this.toDatePicker?.value === "") {
            this.showError(this.getString("The selected date is incomplete or incorrect."));
            this.enableSendButton(false);
            return;
        }

        // Set Max and Mins
        if (this.fromDatePicker?.value && this.toDatePicker?.value) {
            let today = new Date(this.currentTimeInMS);
            let oneYearAgo = new Date(this.currentTimeInMS);
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            let from = new Date(this.fromDatePicker.value + "T00:00:00");
            let to = new Date(this.toDatePicker.value + "T00:00:00");
            let maxToDate = new Date(from);
            maxToDate.setDate(maxToDate.getDate() + 30);

            if (to > today) {
                // Do not let future dates to be chosen
                this.toDatePicker.value = this.fromDatePicker.value;
                this.showError(this.getString("Logs cannot be from a future date."));
                this.enableSendButton(true);
            } else if (from < oneYearAgo) {
                // Do not let dates from more than one year ago to be chosen
                this.fromDatePicker.value = this.toDatePicker.value;
                this.showError(this.getString("Logs cannot be from more than one year ago."));
                this.enableSendButton(true);
            } else if (from > to) {
                // Do not let from date to be after to date
                this.toDatePicker.value = this.fromDatePicker.value;
                this.showError(this.getString("The date “from” must be earlier than the date “to”."));
                this.enableSendButton(true);
            } else if (to > maxToDate) {
                // Do not let date range to be more than 31 days
                this.toDatePicker.value = this.fromDatePicker.value;
                this.showError(this.getString("The date range must be 31 days or shorter."));
                this.enableSendButton(true);
            } else {
                this.hideError();
                this.enableSendButton(true);
            }
        }
    }

    getHTMLElements() {
        if (this.errorBox === null || this.errorText === null ||
            this.spinnerContainer === null || this.transferSpinnerContainer === null || this.transferTable === null ||
            this.fromDatePicker === null || this.toDatePicker === null) {
            this.errorBox = document.getElementById("login-error-box") as HTMLTableRowElement;
            this.errorText = document.getElementById("login-error-text") as HTMLSpanElement;
            this.spinnerContainer = document.getElementById("spinner-container") as HTMLDivElement;
            this.transferSpinnerContainer = document.getElementById("transfer-spinner-container") as HTMLDivElement;
            this.transferTable = document.getElementById("data-transfer-table") as HTMLTableElement;
            this.fromDatePicker = document.getElementById("from-date") as HTMLInputElement;
            this.toDatePicker = document.getElementById("to-date") as HTMLInputElement;
        }
    }

    sendEmail() {
        // Get HTML Elements
        this.getHTMLElements();

        // Get Form Values
        let fromDate = this.fromDatePicker?.value;
        let toDate = this.toDatePicker?.value;
        let emailAddress = (document.getElementById("email-address") as HTMLInputElement).value;

        // Get user and database information
        let currentUser = this.app.userService.getCurrentUser();
        let credentials = this.app.api.getCurrentCredentials();
        let database = credentials ? credentials.credentials.database : "";

        // Start Spinner
        this.showSpinner();

        // Make Api Call
        if (currentUser !== null && fromDate !== undefined && toDate !== undefined) {
            let fromDateAdjusted = new Date(new Date(fromDate).getTime() - dstCalculator.getUtcOffset(new Date(fromDate)) * MINUTES);
            let toDateAdjusted = new Date(new Date(toDate).getTime() - dstCalculator.getUtcOffset(new Date(toDate)) * MINUTES);
            this.app.api.call("SendHosLogsByEmail", {
                driverId: currentUser.id,
                fromDate: fromDateAdjusted.toISOString(),
                toDate: (new Date(toDateAdjusted.getTime() + (1 * DAYS - 1 * SECONDS))).toISOString(),
                providerName: getEldProviderName(database, window.whitelabel, false),
                isTest: isEldTestTransferEnabled(),
                validateOnly: isEldTransferValidateOnly(),
                recipientEmailAddress: emailAddress,
                os: isIphone() ? CanadaEldOperatingSystems.IOS : CanadaEldOperatingSystems.Android,
                serverUrl: geotab.location.host
            }, apiResult => {
                // Stop Spinner
                this.showTable();
                // Handle The API Response
                this.handleResponse(apiResult, emailAddress);
            }, error => {
                // Stop Spinner
                this.showTable();
                // Handle The API Error
                this.handleError(error);
            });
        }
    }

    render () {
        const dataState: DeepReadonlyObject<IDataStore> = this.app.store.getState().data;
        const isFedRamp = dataState.serverFlags === ServerFlags.None;
        if (isFedRamp) {
            this.app.router.navigate("ui/other/main", true);
            return this;
        }

        // Create all dates using the user's timezone-adjusted time
        const currentUserTime = new Date(this.currentTimeInMS);
        const defaultStartUserTime = new Date(this.currentTimeInMS - 30 * DAYS);
        const furthestBackUserTime = new Date(this.currentTimeInMS);
        furthestBackUserTime.setFullYear(furthestBackUserTime.getFullYear() - 1, furthestBackUserTime.getMonth(), furthestBackUserTime.getDate() + 1);

         const templateContext = {
            UserEmail: this.userEmail,
            DefaultStart: this.formatDateForInput(defaultStartUserTime),
            DefaultEnd: this.formatDateForInput(currentUserTime),
            FurthestDateBack: this.formatDateForInput(furthestBackUserTime),
            MostRecentDate: this.formatDateForInput(currentUserTime),
            ButtonDisabled: this.app.store.getState().mobile.online !== NetworkStates.ONLINE
        };

        // Display View
        void this.display(this.template(templateContext));
        return this;
    }

    /* eslint-disable @typescript-eslint/unbound-method */
    events() {
        return {
            "click #sendEmail": this.sendEmail,
            "click #RoadSideCheckTransfer": this.roadSideCheckTransfer,
            "change #from-date": this.checkValidRange,
            "change #to-date": this.checkValidRange
        } as Backbone.EventsHash;
    }
    /* eslint-enable @typescript-eslint/unbound-method */

    // Helper method to format dates
    private formatDateForInput(date: Date): string {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const day = date.getDate();

        const monthStr = month < 10 ? "0" + month : month.toString();
        const dayStr = day < 10 ? "0" + day : day.toString();

        return year + "-" + monthStr + "-" + dayStr;
    }
}